import {
  faExclamation,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import corner6 from 'assets/img/illustrations/corner-6.png';
import draftEntryHelper from 'helpers/draft-entry-helper';
import segmentHelper from 'helpers/segment-helper';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  actionSetDraftSaveError,
  actionSetDraftSaveStatus,
  removeDraftFromServer,
  updateDraftOnServer
} from 'stores/app';
import { showAlert, showConfirm } from 'stores/modal';
import { actionAddNotification } from 'stores/notification';
import {
  actionLogSigningAction,
  actionReInstateSigning,
  actionReset,
  actionStart
} from 'stores/signing';
import c from 'util/const';
import logger from 'util/logger';
import utils from 'util/utils';
import DarftLayout from './DarftLayout';
import {
  useCreateDraftMutation,
  useListDraftsQuery,
  useRemoveDraftMutation
} from 'rtk/draft.slice';

const Drafts = () => {
  const dispatch = useDispatch();
  const { userMaster } = useSelector(state => state.app);
  const navigate = useNavigate();
  const { urlDraftId } = useParams();

  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [sortColumnId, setSortColumnId] = useState('title');
  const [sortDesc, setSortDesc] = useState(true);

  const orderBy =
    sortColumnId === 'title'
      ? 'Updated'
      : sortColumnId === 'date'
      ? 'Created'
      : 'Updated';
  const orderDirection = sortDesc ? 'desc' : 'asc';

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const skip = pageIndex * pageSize;
  const {
    data: serverDraftsRes,
    isLoading: isRefreshingDrafts,
    isError: serverDraftsError,
    isFetching: isFetchingServerDrafts,
    error: serverDraftsErrorObj,
    refetch: refetchServerDrafts
  } = useListDraftsQuery(
    {
      skip,
      pageSize,
      includeData: true,
      returnMeta: true,
      orderBy,
      orderDirection
    },
    { skip: !userMaster || !!urlDraftId }
  );

  const totalRecords = serverDraftsRes?.TotalRecords ?? 0;
  const endOfResults = !!serverDraftsRes?.EndOfResults;
  const isDraftSynced =
    Boolean(userMaster) && serverDraftsRes !== undefined && !isRefreshingDrafts;

  const getDraftSigners = entryData => {
    let signers = entryData?.signers || [];
    let signersArray = [];
    if (signers) {
      signersArray = _.filter(signers, s => !s.isDocumentWitness).map(s => ({
        id: s.signer_id,
        name: s.first_name + ' ' + s.last_name,
        size: 'xl'
      }));
    }
    return signersArray;
  };

  const listOurDraft = draftList => {
    let draftItemArray = [];
    _.map(draftList, d => {
      if (d !== null) {
        let progress = draftEntryHelper.getDraftStepName(
          d.draft_metadata?.entry_step
        );
        let draftItem = {
          id: d.draft_id,
          title: moment(d.draft_metadata?.last_updated)
            .utc()
            .format('MM/DD/YYYY HH:mm A'),
          last_updated: d.draft_metadata?.last_updated,
          avatar: {
            name: 'C',
            size: 'xl'
          },
          projectName: progress.label,
          step: progress.id,
          documents: d.entry_data?.notarization_documents?.length || 'N/A',
          members: getDraftSigners(d.entry_data),
          progress: progress.value,
          color: progress.color,
          date: moment(d.draft_metadata?.created)
            .utc()
            .format('MM/DD/YYYY HH:mm A'),
          action: d.draft_id,
          isCompleted: !!d.draft_metadata?.completed_at,
          location: d.entry_data?.location || null,
          source: d.draft_metadata?.source || 'Desktop'
        };
        draftItemArray.push(draftItem);
      }
    });
    return draftItemArray;
  };

  const draftEntry = useMemo(() => {
    const objects = serverDraftsRes?.Objects || [];
    const pageDrafts = [];

    _.forEach(objects, entry => {
      const serverDraft = utils.decodeBlobData(entry.Body);
      if (serverDraft) {
        pageDrafts.push({
          draft_id: draftEntryHelper.getDarftIdFromKey(entry.GUID),
          draft_metadata: serverDraft?.draft_data,
          entry_data: serverDraft?.entry_data
        });
      } else {
        logger.log('Server draft is null', serverDraft);
      }
    });

    return listOurDraft(pageDrafts);
  }, [serverDraftsRes]);

  const [createDraft] = useCreateDraftMutation();
  const [removeDraftApi] = useRemoveDraftMutation();

  const onSortChange = sortByArr => {
    logger.log('Draft sort changed', sortByArr);
    const next = Array.isArray(sortByArr) ? sortByArr[0] : null;
    if (!next?.id) return;
    setSortColumnId(next.id);
    setSortDesc(!!next.desc);
    setPageIndex(0);
  };

  useEffect(() => {
    if (userMaster && !urlDraftId) {
      refreshDraftFromServer();
    } else if (urlDraftId) {
      logger.log('Found urlDraftId to edit', urlDraftId);
      setDraftToEdit(draftEntryHelper.getDarftIdFromKey(urlDraftId));
    }
  }, [userMaster, urlDraftId]);

  // useEffect(() => {
  //   draftEntryHelper.refreshLocalDraft(userMaster?.GUID).then(res => {
  //     refreshDraftFromServer();
  //   });
  // }, []);

  const refreshDraftFromServer = () => {
    if (userMaster) {
      setPageIndex(0);
      refetchServerDrafts();
    }
  };

  // useEffect(() => {
  //   if (userMaster) {
  //     refreshDraftEntry();
  //   }
  // }, [isDraftSynced]);

  useEffect(() => {
    if (serverDraftsError) {
      logger.log('Draft refresh failed', serverDraftsErrorObj);
      toast.warning("Draft couldn't refresh properly. Try again!", {
        theme: 'colored',
        position: 'bottom-center',
        icon: faExclamationTriangle,
        toastId: 'login-success'
      });
    }
  }, [serverDraftsError, serverDraftsErrorObj]);

  const createNewDraftIdOrWarn = () => {
    return new Promise(resolve => {
      draftEntryHelper
        .getNewDraftId()
        .then(() => {
          createDraft({})
            .unwrap()
            .then(res => {
              logger.log('createNewDraftIdOrWarn Draft', res);
              dispatch(actionSetDraftSaveStatus(c.draftStatus.None));
              resolve(c.storage.draftEntryIdPrefix + res.GUID);
            })
            .catch(() => {
              dispatch(
                showAlert(
                  'Drafts not available',
                  'We were unable to create a new draft on your device.  Your signing will not be saved until uploaded to our server.'
                )
              );
              resolve(null);
            });
        })
        .catch(() => {
          //logger.log('createNewDraftIdOrWarn catch error', err);
          dispatch(
            showAlert(
              'Drafts not available',
              'We were unable to create a new draft on your device.  Your signing will not be saved until uploaded to our server.'
            )
          );
          resolve(null);
        });
    });
  };

  const setDraftToEdit = async draft_id => {
    draftEntryHelper
      .getDraftFromServer(draft_id)
      .then(draft => {
        if (draft === null) {
          navigate('/notary/signing');
          logger.log('setDraftToEdit Draft', draft_id);
          toast.error("Draft doesn't exist.", {
            theme: 'colored',
            position: 'bottom-center',
            icon: faExclamation
          });
        } else {
          return new Promise(resolve => {
            if (draft && !_.isEmpty(draft)) {
              let draftMetadata = draft.draft_metadata;
              let draftEntrydata = draft.entry_data;

              let currentStepVal = c.entrySteps.STEP_1;
              if (
                !!draft.draft_metadata.completed_at ||
                !!draftEntrydata.completedAtDate
              ) {
                currentStepVal = c.entrySteps.STEP_6;
              } else {
                currentStepVal = draftMetadata.entry_step;
              }
              draftEntryHelper.getDraftStepName(currentStepVal);

              dispatch(actionSetDraftSaveError(null));
              dispatch(actionSetDraftSaveStatus(c.draftStatus.None));

              if (draftEntrydata.draft_id) {
                dispatch(actionReInstateSigning(draftEntrydata));
                navigate(
                  '/notary/signing/' +
                    draftEntryHelper.getDarftKeyFromId(draft_id)
                );
                resolve(true);
              } else {
                //logger.log('No draft found in signig, saving skipped');
              }
            } else {
              logger.warn(
                'draft was null or empty',
                userMaster?.GUID,
                draft_id,
                draft
              );
              resolve(null);
            }
          });
        }
      })
      .then(() => {});
  };

  const removeDraft = draft_id => {
    dispatch(
      showConfirm(
        'Are you sure?',
        'All data associated with this draft, including signatures, documents & identifications will be deleted. This action can not be undone.',
        res => {
          if (res) {
            removeDraftApi(draftEntryHelper.getDarftKeyFromId(draft_id))
              .unwrap()
              .then(() => {
                segmentHelper.track(segmentHelper.events.DELETE_DRAFT_CLICKED);
                refetchServerDrafts();
              })
              .catch(() => {
                dispatch(
                  removeDraftFromServer(
                    draftEntryHelper.getDarftKeyFromId(draft_id)
                  )
                ).then(() => refetchServerDrafts());
              });

            dispatch(
              actionAddNotification({
                message: 'Deleted Journal Entry from draft',
                name: userMaster.FirstName + ' ' + userMaster.LastName
              })
            );
          }
        }
      )
    );
  };

  const removeMultipleDrafts = (draft_ids, onComplete) => {
    if (!draft_ids || draft_ids.length === 0) return;
    const count = draft_ids.length;
    dispatch(
      showConfirm(
        'Delete drafts?',
        `Delete ${count} draft${
          count === 1 ? '' : 's'
        }? This action cannot be undone.`,
        async res => {
          if (!res) return;

          await Promise.all(
            draft_ids.map(draft_id =>
              removeDraftApi(draftEntryHelper.getDarftKeyFromId(draft_id))
                .unwrap()
                .catch(() =>
                  dispatch(
                    removeDraftFromServer(
                      draftEntryHelper.getDarftKeyFromId(draft_id)
                    )
                  )
                )
            )
          );

          segmentHelper.track(segmentHelper.events.DELETE_DRAFT_CLICKED);
          refetchServerDrafts();

          dispatch(
            actionAddNotification({
              message: `Deleted ${count} draft${
                count === 1 ? '' : 's'
              } from drafts`,
              name: userMaster.FirstName + ' ' + userMaster.LastName
            })
          );

          onComplete && onComplete();
        }
      )
    );
  };

  const createNewDraftEntry = () => {
    segmentHelper.track(segmentHelper.events.CREATE_NEW_ENTRY_CLICKED);
    let draft_id = null;
    dispatch(actionReset());
    dispatch(actionSetDraftSaveError(null));
    createNewDraftIdOrWarn().then(newDraftId => {
      draft_id = newDraftId;

      let now = moment().format();

      // Seed the new server draft with the initial signing_timestamp so the
      // pre-populated Notarization Date & Time survives the re-hydration that
      // fires when we navigate into the draft (setDraftToEdit -> getDraftFromServer).
      let draftMetadata = {
        created: now,
        completed_at: null,
        last_updated: now,
        entry_step: c.entrySteps.STEP_1,
        source: 'Desktop'
      };
      let updatedSavedDraftPromise = draft_id
        ? dispatch(
            updateDraftOnServer(
              draftEntryHelper.getDarftKeyFromId(draft_id),
              utils.encodeObjectForBlobData({
                draft_id: draft_id,
                draft_data: draftMetadata,
                entry_data: { signing_timestamp: now }
              })
            )
          ).catch(() => true)
        : Promise.resolve(true);

      Promise.all([updatedSavedDraftPromise]).then(() => {
        let signingResetPromise = new Promise(resolve => {
          dispatch(actionReset());
          resolve(true);
        });

        Promise.all([signingResetPromise]).then(() => {
          let signingActionStartPromise = new Promise(resolve => {
            dispatch(
              actionStart({
                draft_id: draft_id,
                promptForRatingOnComplete: false,
                startedAtDate: now,
                signing_timestamp: now
              })
            );
            resolve(true);
          });

          Promise.all([signingActionStartPromise]).then(() => {
            dispatch(actionLogSigningAction(`Signing Created.`));
            navigate(
              '/notary/signing/' + draftEntryHelper.getDarftKeyFromId(draft_id)
            );
          });

          dispatch(
            actionAddNotification({
              message: 'Started New Journal Entry',
              to:
                '/notary/signing/' +
                draftEntryHelper.getDarftKeyFromId(draft_id)
            })
          );
        });
      });
    });
  };

  return (
    <>
      {/* <PageHeader
        title="Saved Drafts"
        className="mb-3"
        titleTag="h2"
        image={corner6}
        col={{ lg: 8, md: 7, sm: 12 }}
        eCol={{ lg: 4, md: 5, sm: 12 }}
        eol={<></>}
      /> */}
      <DarftLayout
        draftEntry={[...draftEntry]}
        createNewDraftEntry={createNewDraftEntry}
        removeDraft={removeDraft}
        removeMultipleDrafts={removeMultipleDrafts}
        isDraftSynced={isDraftSynced}
        refreshDraftFromServer={refreshDraftFromServer}
        isRefreshingDrafts={isRefreshingDrafts}
        sortBy={[]}
        onSortChange={onSortChange}
        isFetchingServerDrafts={isFetchingServerDrafts}
        image={corner6}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRecords={totalRecords}
        endOfResults={endOfResults}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
      />
    </>
  );
};

Drafts.propTypes = {
  variant: PropTypes.oneOf(['pills']),
  validation: PropTypes.bool,
  progressBar: PropTypes.bool
};

export default Drafts;
